import React, { ClipboardEvent } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { FastField, FormikProps, useFormikContext } from "formik";
import { Data } from "../store/terminal/types";
import { connect } from "react-redux";
import { get } from "lodash";
import { ConfigPropExtenderHoc } from "./StaticConfigProvider";
import OptionalToolTip from "./toolTip/OptionalToolTip";
import SectionWrapper from "./pageSections/SectionWrapper";
import TextInput from "./formElements/TextInput";
import CustomizedSwitch from "./formElements/Switch";
import { getValueForField } from "../pages/terminal/terminalEditForm/terminalEditFormComponents/editConfigurationComponent/ConfigurationOptions";

interface TippingV2Props {
  toolTips?: { terminals?: { options?: Record<string, string> } };
  isUpdateAllowed?: boolean;
}

const TippingV2 = (props: TippingV2Props) => {
  const formProps = useFormikContext();
  const { values, setFieldValue } = formProps as FormikProps<Data>;

  const { isUpdateAllowed } = props;

  const tippingV2 = values.attributes.TippingV2;
  const TippingMode = tippingV2?.TippingMode;
  const { EnableTippingV2 } = values.attributes;
  const isTippingDisabled = EnableTippingV2 === "No";
  const isTippingByPercentage = TippingMode === "Percentage";

  const digitsOnly = /^[0-9]*$/;

  const percentage1Tooltip = "Percentage 1";
  const percentage2Tooltip = "Percentage 2";
  const percentage3Tooltip = "Percentage 3";

  const validateOnPaste = (e: ClipboardEvent) => {
    const copiedText = e.clipboardData.getData("text");
    if (!digitsOnly.test(copiedText)) {
      e.preventDefault();
      return false;
    }
  };

  const checkedTippingV2Mode =
    values &&
    values.attributes &&
    get(values, "attributes.EnableTippingV2") === "Yes";

  const endAdornment = (
    <InputAdornment position="end">
      {isTippingByPercentage ? "%" : "$"}
    </InputAdornment>
  );

  return (
    <SectionWrapper isChild={true} title="Tipping">
      <Grid container spacing={2} xs={12}>
        <Grid item xs={12} md={12}>
          <Box p={2} pt={0}>
            <FormControlLabel
              style={{ marginRight: "3rem" }}
              control={
                <CustomizedSwitch
                  name={"attributes.EnableTippingV2"}
                  checked={checkedTippingV2Mode}
                  onChange={(event, value) => {
                    setFieldValue(
                      "attributes.EnableTippingV2",
                      getValueForField("attributes.EnableTippingV2", value)
                    );
                  }}
                  disabled={!isUpdateAllowed}
                />
              }
              label="Enable Tipping"
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="tippingMode">Tipping Mode</InputLabel>
            <FastField
              as={Select}
              name="attributes.TippingV2.TippingMode"
              labelId="tippingMode"
              label="Tipping Mode"
              variant="outlined"
              key={`TippingMode_${EnableTippingV2}`}
              disabled={isTippingDisabled || !isUpdateAllowed}
            >
              <MenuItem value="Percentage">Percentage</MenuItem>
              <MenuItem value="Amount">Amount</MenuItem>
            </FastField>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <OptionalToolTip
            tooltip={get(
              props,
              `toolTips.terminals.options.surcharge.["${percentage1Tooltip}"]`
            )}
          >
            <TextInput
              type="number"
              fieldname={`attributes.TippingV2.TipWithPercentage1`}
              InputProps={{ endAdornment }}
              disabled={isTippingDisabled || !isUpdateAllowed}
              key={`TipWithPercentage1_${EnableTippingV2}`}
              label={isTippingByPercentage ? "Percentage 1" : "Amount 1"}
              onPaste={validateOnPaste}
            />
          </OptionalToolTip>
        </Grid>
        <Grid item xs={12} md={3}>
          <OptionalToolTip
            tooltip={get(
              props,
              `toolTips.terminals.options.surcharge.["${percentage2Tooltip}"]`
            )}
          >
            <TextInput
              type="number"
              fieldname="attributes.TippingV2.TipWithPercentage2"
              InputProps={{ endAdornment }}
              disabled={isTippingDisabled || !isUpdateAllowed}
              key={`TipWithPercentage2_${EnableTippingV2}`}
              label={isTippingByPercentage ? "Percentage 2" : "Value 2"}
              onPaste={validateOnPaste}
            />
          </OptionalToolTip>
        </Grid>
        <Grid item xs={12} md={3}>
          <OptionalToolTip
            tooltip={get(
              props,
              `toolTips.terminals.options.surcharge.["${percentage3Tooltip}"]`
            )}
          >
            <TextInput
              type="number"
              fieldname="attributes.TippingV2.TipWithPercentage3"
              InputProps={{ endAdornment }}
              disabled={isTippingDisabled || !isUpdateAllowed}
              key={`TipWithPercentage3_${EnableTippingV2}`}
              label={isTippingByPercentage ? "Percentage 3" : "Value 3"}
              onPaste={validateOnPaste}
            />
          </OptionalToolTip>
        </Grid>
      </Grid>
    </SectionWrapper>
  );
};

const ConnectedTippingV2 = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: TippingV2Props) => {
    return {
      ...ownProps,
    };
  }
)(TippingV2);

const ConnectedTippingV2Hoc = (props: TippingV2Props) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedTippingV2 {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedTippingV2Hoc;
